import { AxiosInstance, AxiosResponse } from 'axios';
import * as Sentry from '@sentry/react';
import { config } from 'config';
import { VehicleMatch, VehicleType, VehicleVersion, VehicleVersionChoiceType } from 'types';
import { AxiosClient, AxiosUtils } from '../AxiosClient/AxiosClient';

export type MatchImmatResponse = {
  vehicleVersionChoiceType: VehicleVersionChoiceType;
  vehicleVersion: VehicleVersion;
  vehicleMatchList?: VehicleMatch[];
  mra: string;
};

class MatchImmat {
  private readonly client: AxiosInstance;

  constructor() {
    this.client = AxiosClient.get({
      baseURL: config.URL_MATCH_IMMAT,
      headers: {
        'x-client-source': 'classified:lcab:depot-web',
      },
    });
  }

  async match(vehicleType: VehicleType, immat: string): Promise<MatchImmatResponse> {
    // TODO: Remove vehicleType === 'MOTO'
    if (vehicleType === 'MOTO' || !immat) {
      return {
        vehicleVersionChoiceType: VehicleVersionChoiceType.DECLUTCH,
        vehicleVersion: {},
        mra: 'NOT_AVAILABLE',
      };
    }

    return this.client
      .post(
        'v1/vehicles/match-immat',
        {
          immat,
        },
        {
          params: {
            size: 30,
          },
          headers: AxiosClient.getAuthorizationHeader(),
        },
      )
      .then(result => {
        const data: any = result.data;
        const mra = this.matchStatusCodeToMRA(result.status);
        const vehicleVersionChoiceType = VehicleVersionChoiceType.DECLUTCH;
        // Mode matché
        if (data.matchStatus === 'VERSION_OK' && data.versionOk && data.vehicleMatchList.length > 0) {
          const ngcVehicle = data.ngcResponse.ngcVehicle;
          return {
            vehicleVersionChoiceType: VehicleVersionChoiceType.MATCH,
            vehicleVersion: {
              firstCirculationDate: new Date(data.firstTrafficDate),
              category: ngcVehicle.categoryDAB,
              energy: ngcVehicle.energyDAB,
              doors: Number.parseInt(ngcVehicle.nbPortes),
              // TODO: When vehicleType === 'MOTO' is removed uncomment
              //...(vehicleType !== 'MOTO' && { doors: Number.parseInt(ngcVehicle.nbPortes) }),
              gearbox: ngcVehicle.gearboxDAB,
              fiscalHorsePower: Number.parseInt(ngcVehicle.puisFisc),
            },
            vehicleMatchList: data.vehicleMatchList.map((match: any) => ({
              ...match,
              item: { ...match.item, trimLevel: match.item.dabVersion.trimLevel },
            })),
            mra,
          };
        }

        // Mode debrayé
        if (data.matchStatus === 'KO') {
          if (data.makeOk !== undefined && data.makeOk && data.vehicleMatchList.length === 0) {
            const firstCirculationDate = new Date(data.firstTrafficDate || data.requestProcessed.firstTrafficDate);
            const make = data.make;
            if (data.modelOk !== undefined && data.modelOk) {
              return {
                vehicleVersionChoiceType,
                vehicleVersion: { firstCirculationDate, make, model: data.model },
                mra,
              };
            }
            return {
              firstCirculationDate,
              vehicleVersionChoiceType,
              vehicleVersion: { firstCirculationDate, make },
              mra,
            };
          }
        }

        return {
          vehicleVersionChoiceType,
          vehicleVersion: {},
          mra,
        };
      })
      .catch(r => {
        Sentry.captureException(r, {
          tags: {
            api: 'Matching-immat',
            endpoint: 'v1/vehicles/match-immat',
            ...AxiosUtils.enrichTags(r),
          },
        });
        let mra = 'RETRY';
        if (r.response) {
          const response = r.response as AxiosResponse;
          mra = this.matchStatusCodeToMRA(response.status);
        }
        return {
          vehicleVersionChoiceType: VehicleVersionChoiceType.DECLUTCH,
          vehicleVersion: {},
          mra,
        };
      });
  }

  private matchStatusCodeToMRA(statusCode: number): string {
    switch (statusCode) {
      case 200:
        return 'OUTSTANDING';
      case 207:
        return 'GOOD';
      case 206:
        return 'BAD';
      case 404:
        return 'NOT_AVAILABLE';
      case 406:
        return 'NOT_AVAILABLE';
      default:
        return 'RETRY';
    }
  }
}

export const MatchImmatService = new MatchImmat();
